// src/lib/db/firestore.js
// PATCHES IN THIS VERSION:
//
//   FIX #13 (this version): firestoreSubscribe now accepts a getter function
//     for the trustFirstNull option in addition to a plain boolean.
//
//     WHY: initSync (syncManager.js) sets its trustFirstNull flag via an async
//     IDB check that resolves AFTER the Firestore subscription is already
//     running. Previously, firestoreSubscribe captured trustFirstNull as a
//     boolean at subscribe-creation time — always false, because the async
//     IDB check hadn't resolved yet. This meant the first null snapshot
//     (a cross-device delete arriving after an offline gap) was always
//     suppressed, leaving stale data visible on the reconnecting device.
//
//     Fix: if trustFirstNull is a function, firestoreSubscribe calls it at
//     each snapshot event to read the current value. initSync can now pass
//     a getter closure over a plain object, which the async IDB check updates
//     when it resolves. The boolean path is unchanged — all other callers are
//     unaffected.
//
// ── PREVIOUS PATCHES (retained) ──────────────────────────────────────────────
//
//   FIX #8: Exported isQuotaError() helper.
//     resource-exhausted is a distinct failure mode from network errors.
//     syncManager.flushPendingQueue imports isQuotaError and activates a
//     1-hour backoff on encountering it.
//
//   FIX #7: firestoreSubscribe accepts trustFirstNull option to allow
//     cross-device tombstones to be delivered after an offline gap.
//
// All other logic is unchanged from the previous version.
import {
  doc,
  getDoc,
  setDoc,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '@/lib/firebase';

// ── Collection path constants ─────────────────────────────────
// Exported so syncManager.js (and any future callers) can import these
// rather than duplicating the strings. A single point of truth prevents
// silent path divergence between reads (firestoreSubscribe) and writes
// (flushPendingQueue's writeBatch).
export const FIRESTORE_USERS_COLLECTION = 'users';
export const FIRESTORE_DATA_COLLECTION  = 'data';

// ── Firebase error codes that mean "offline / not yet available" ──────────────
const OFFLINE_ERROR_CODES = new Set([
  'unavailable',
  'failed-precondition',
  'cancelled',
  // Note: resource-exhausted is NOT in this set — it is a quota error and must
  // be handled differently (backoff, not just "try again when online").
]);

function isOfflineError(err) {
  return (
    OFFLINE_ERROR_CODES.has(err?.code) ||
    OFFLINE_ERROR_CODES.has(err?.code?.replace('firestore/', '')) ||
    err?.message?.includes('Failed to get document because the client is offline')
  );
}

/**
 * FIX #8: Returns true if the error signals that the daily Firestore write
 * quota is exhausted. This is distinct from a transient network error — retrying
 * immediately will NOT succeed and will waste further quota reads.
 *
 * Exported so syncManager can activate a 1-hour backoff instead of retrying
 * every 5 minutes.
 *
 * @param {Error} err
 * @returns {boolean}
 */
export function isQuotaError(err) {
  return (
    err?.code === 'resource-exhausted' ||
    err?.code === 'firestore/resource-exhausted' ||
    err?.message?.toLowerCase().includes('resource-exhausted') ||
    err?.message?.toLowerCase().includes('quota exceeded')
  );
}

function getUid() {
  return auth?.currentUser?.uid ?? null;
}

function docRef(uid, key) {
  return doc(db, FIRESTORE_USERS_COLLECTION, uid, FIRESTORE_DATA_COLLECTION, key);
}

/**
 * Resolves with the Firebase user once auth state is known.
 */
function waitForAuth() {
  return new Promise((resolve) => {
    if (!auth) { resolve(null); return; }
    const unsub = onAuthStateChanged(auth, (user) => {
      unsub();
      resolve(user);
    });
  });
}

/**
 * Write a value to Firestore.
 * Passing null stores a tombstone { value: null }.
 */
export async function firestoreSet(key, value) {
  const uid = getUid();
  if (!uid) return;

  await setDoc(docRef(uid, key), {
    value,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

/**
 * Read a value from Firestore, waiting for auth to resolve first.
 * Returns null if not authenticated, document doesn't exist, or times out.
 *
 * @param {string} key
 * @param {number} [timeoutMs=5000]
 */
export async function firestoreGet(key, timeoutMs = 5000) {
  const user = await waitForAuth();
  if (!user) return null;

  const fetchPromise = getDoc(docRef(user.uid, key)).then((snap) => {
    if (!snap.exists()) return null;
    const data = snap.data();
    return 'value' in data ? data.value : null;
  });

  const timeoutPromise = new Promise((_, reject) =>
    setTimeout(() => reject(new Error(`[FIREBASE] firestoreGet("${key}") timed out after ${timeoutMs}ms`)), timeoutMs)
  );

  return Promise.race([fetchPromise, timeoutPromise]);
}

/**
 * Subscribe to real-time Firestore changes for a key.
 *
 * @param {string}   key
 * @param {Function} callback         — called with value or null on delete
 * @param {object}   [options]
 * @param {boolean|Function} [options.trustFirstNull=false]
 *   Controls whether a !snap.exists() on the FIRST snapshot is delivered to
 *   the callback (treating it as a genuine remote delete / tombstone).
 *
 *   Accepts a plain boolean OR a getter function () => boolean. The getter
 *   form is used by syncManager.initSync, which determines the value via an
 *   async IDB check that may complete AFTER the subscription has started.
 *   Passing a getter ensures the flag is read at event-fire time rather than
 *   at subscription-creation time.
 *
 *   When true/truthy: a first !snap.exists() event IS delivered — use when
 *     the caller confirmed local IDB data exists, so Firestore "not exists"
 *     must mean the key was deleted on another device while offline.
 *   When false (default): the first !snap.exists() is suppressed — protects
 *     local IDB data from being wiped by the new-key subscription race where
 *     the snapshot fires before our own write propagates to Firestore.
 *
 * @returns {Function} unsubscribe
 */
export function firestoreSubscribe(key, callback, { trustFirstNull = false } = {}) {
  // FIX #13: resolve trustFirstNull at event time, not subscription time.
  // Accepts a getter function so async callers (initSync) can set the value
  // after the IDB check completes without recreating the subscription.
  const resolveTrust = typeof trustFirstNull === 'function'
    ? trustFirstNull
    : () => trustFirstNull;

  let unsubscribeSnapshot = () => {};
  let cancelled = false;

  // Guard: if Firebase auth is null (missing/invalid API key), skip subscription
  if (!auth) {
    return () => {};
  }

  const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
    unsubscribeSnapshot();
    unsubscribeSnapshot = () => {};

    if (cancelled) return;
    if (!user) return;

    const ref = docRef(user.uid, key);

    // hasReceivedRealValue: guards against the new-key subscription race
    // where the first snapshot fires !snap.exists() before the local write
    // propagates to Firestore.
    // FIX #7/#13: resolveTrust() is called at event time so dynamic flags work.
    let hasReceivedRealValue = false;
    let isFirstEvent = true;

    unsubscribeSnapshot = onSnapshot(ref, (snap) => {
      const wasFirst = isFirstEvent;
      isFirstEvent = false;

      if (!snap.exists()) {
        // Deliver null if:
        //   a) we previously saw a real value in this subscription, OR
        //   b) this is the first event AND resolveTrust() returns true —
        //      meaning the caller confirmed local IDB data exists, so
        //      Firestore "not exists" is a genuine cross-device delete.
        if (hasReceivedRealValue || (wasFirst && resolveTrust())) {
          callback(null);
        }
        return;
      }

      hasReceivedRealValue = true;
      const data = snap.data();
      callback('value' in data ? data.value : null);
    }, (err) => {
      if (isOfflineError(err)) {
        console.warn(`[FIREBASE] onSnapshot offline/unavailable for "${key}" (expected when offline):`, err.code);
      } else if (isQuotaError(err)) {
        // Quota exhausted on a listener — log clearly, don't treat as a crash.
        // The listener will resume automatically once quota resets at midnight.
        console.warn(`[FIREBASE] onSnapshot quota exhausted for "${key}" — will resume at quota reset:`, err.code);
      } else {
        console.error(`[FIREBASE] onSnapshot error for "${key}":`, err);
      }
    });
  });

  return () => {
    cancelled = true;
    unsubscribeAuth();
    unsubscribeSnapshot();
  };
}